import { createContext, useContext, useMemo, type ReactNode } from "react"

import { createPortal } from "react-dom"

import { NotificationUI } from "./NotificationUI"
import { useErrorNotificationState } from "./useErrorNotificationState"

import type { ErrorNotificationContextType } from "./types"

const ErrorNotificationContext = createContext<
	ErrorNotificationContextType | undefined
>(undefined)

export const useErrorNotification = () => {
	const context = useContext(ErrorNotificationContext)
	if (context === undefined) {
		throw new Error(
			"useErrorNotification must be used within an ErrorNotification provider"
		)
	}
	return context
}

export default function ErrorNotification({
	children,
}: {
	children: ReactNode
}) {
	const {
		notifications,
		displayMode,
		addError,
		addInfo,
		dismissError,
		clearAll,
		handleBannerClick,
		handleStackedClick,
	} = useErrorNotificationState()

	const ctx = useMemo(() => ({ addError, addInfo }), [addError, addInfo])

	return (
		<ErrorNotificationContext.Provider value={ctx}>
			{children}
			{displayMode !== "hidden" &&
				createPortal(
					<NotificationUI
						notifications={notifications}
						displayMode={displayMode}
						dismissError={dismissError}
						clearAll={clearAll}
						handleBannerClick={handleBannerClick}
						handleStackedClick={handleStackedClick}
					/>,
					document.body
				)}
		</ErrorNotificationContext.Provider>
	)
}
